import { ref, onMounted, watch } from "@vue/composition-api";
import { axisBottom, axisLeft, select } from "d3";
import TickLabelFormatService from "../../services/TickLabelFormatService";
import { DefaultConfigs, IPadProConfigs } from "../../services/DefaultConfigs";
import { getTextHeight } from "../../services/Canvas2DContext";

function getTicks(high, low, inc, count) {
  if (low + inc * count < high) {
    count++;
    return getTicks(high, low, inc, count);
  }

  return count;
}

export default function useBarChartAxes(xRef, yRef, configRef, xAxisGroupRef, yAxisGroupRef, widthRef, heightRef, isIPadProRef) {
  const lowestY = ref(0);
  const xAxisOriginLabel = ref(null);
  const xAxis = axisBottom();
  const yAxis = axisLeft();

  const updateAxes = () => {
    const xAxisConfig = configRef?.value?.xAxis;
    const yAxisConfig = configRef?.value?.yAxis;
    if (!xAxisConfig || !yAxisConfig || !xRef?.value || !yRef?.value) {
      return;
    }

    const { originLabel } = xAxisConfig;
    if (originLabel) {
      xAxisOriginLabel.value = {
        ...(isIPadProRef?.value ? IPadProConfigs.XAxisOriginLabel : DefaultConfigs.XAxisOriginLabel),
        ...originLabel,
      };

      if (xAxisOriginLabel.value.y == null) {
        // if we did not specify a `y` property in the source JSON, we need to compute
        // the it _after_ the objects are combined because if the font is overriden
        // from the JSON, we want to respect that when computing the text height
        xAxisOriginLabel.value.y = getTextHeight(xAxisOriginLabel.value.font);
      }
    } else {
      xAxisOriginLabel.value = null;
    }

    const tickCalc = getTicks(yAxisConfig.high, yAxisConfig.low, yAxisConfig.inc, 1);
    const tickFormat = (domain) => xAxisConfig.overrides?.[domain] ?? domain;
    const xTickSize = xAxisConfig.tickSize ?? DefaultConfigs.BarChart.xAxis.tickSize;
    const yTickSize = yAxisConfig.tickSize ?? DefaultConfigs.TickSize;
    const xLabelSpace = xAxisConfig.tickLabelSpace ?? DefaultConfigs.BarChart.xAxis.labelSpace;

    yAxis.scale(yRef.value)
      .tickSize(yTickSize)
      .ticks(tickCalc);

    xAxis.scale(xRef.value)
      .tickSize(xTickSize)
      .tickFormat(tickFormat);

    lowestY.value = yRef.value(yAxisConfig.low);

    select(xAxisGroupRef.value)
      .call(xAxis)
      .selectAll(".tick text")
        .style("font", isIPadProRef?.value ? IPadProConfigs.XAxisTicks.font : DefaultConfigs.XAxisTicks.font)
        .style("fill", DefaultConfigs.XAxisTicks.textColor)
        .call(TickLabelFormatService.postFormatTickLabel, xTickSize + xLabelSpace);

    select(yAxisGroupRef.value)
      .call(yAxis)
      .selectAll(".tick text")
        .style("font", isIPadProRef?.value ? IPadProConfigs.YAxisTicks.font : DefaultConfigs.YAxisTicks.font)
        .style("fill", DefaultConfigs.YAxisTicks.textColor);
  };

  watch([configRef, xAxisGroupRef, yAxisGroupRef, widthRef, heightRef, xRef, yRef, isIPadProRef], updateAxes);

  onMounted(updateAxes);

  return { lowestY, xAxisOriginLabel };
}
